import { useState, useRef, useCallback } from 'react';
import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Button, Card, Badge, Pagination } from '@/components/common';
import { useUploadBatchQuote, useBatchStatus, useBatchHistory } from '@/hooks/useIntegrations';
import { integrationsService } from '@/services/integrations';
import { formatRelativeTime } from '@/utils';

type Tab = 'upload' | 'history';

function getStatusVariant(status: string) {
  switch (status) {
    case 'pending':
      return 'warning' as const;
    case 'processing':
      return 'info' as const;
    case 'completed':
      return 'success' as const;
    case 'failed':
      return 'error' as const;
    default:
      return 'default' as const;
  }
}

export function BatchQuotePage() {
  const [activeTab, setActiveTab] = useState<Tab>('upload');
  const [historyPage, setHistoryPage] = useState(1);

  return (
    <div>
      <PageHeader
        title="Batch Quotes"
        subtitle="Upload CSV files to process multiple quote requests at once"
        breadcrumbs={[{ label: 'Quotes', href: '/quotes' }, { label: 'Batch Upload' }]}
      />

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        {(['upload', 'history'] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 text-sm font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'border-b-2 border-primary-600 text-primary-600 dark:text-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab === 'upload' ? 'Upload New' : 'View History'}
          </button>
        ))}
      </div>

      {activeTab === 'upload' ? (
        <UploadTab />
      ) : (
        <HistoryTab page={historyPage} onPageChange={setHistoryPage} />
      )}
    </div>
  );
}

function UploadTab() {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadBatchQuote();
  const { data: batchStatus } = useBatchStatus(activeBatchId ?? '');

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) validateAndSetFile(dropped);
  }, []);

  function validateAndSetFile(f: File) {
    if (!f.name.endsWith('.csv')) {
      toast.error('Only .csv files are allowed');
      return;
    }
    setFile(f);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0];
    if (selected) validateAndSetFile(selected);
  }

  async function handleUpload() {
    if (!file) return;
    uploadMutation.mutate(file, {
      onSuccess: (data) => {
        setActiveBatchId(data.batch_id || data.id);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  }

  async function handleDownload() {
    if (!activeBatchId) return;
    try {
      const blob = await integrationsService.downloadBatchResults(activeBatchId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch-results-${activeBatchId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download results');
    }
  }

  const completedPct =
    batchStatus && batchStatus.total_items > 0
      ? Math.round((batchStatus.completed_items / batchStatus.total_items) * 100)
      : 0;

  return (
    <div className="space-y-6">
      {/* Drop zone */}
      <Card>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
            isDragging
              ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileChange}
          />
          <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          {file ? (
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{file.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Drag and drop your CSV file here
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">or click to browse</p>
            </div>
          )}
        </div>

        {file && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-700 dark:text-gray-300">{file.name}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <Button
              onClick={handleUpload}
              isLoading={uploadMutation.isPending}
              disabled={uploadMutation.isPending}
              leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
            >
              Upload &amp; Process
            </Button>
          </div>
        )}
      </Card>

      {/* Progress section */}
      {batchStatus && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Batch Progress</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">ID: {activeBatchId}</p>
            </div>
            <Badge variant={getStatusVariant(batchStatus.status)} size="sm">
              {batchStatus.status.charAt(0).toUpperCase() + batchStatus.status.slice(1)}
            </Badge>
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>
                {batchStatus.completed_items} / {batchStatus.total_items} items
              </span>
              <span>{completedPct}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${
                  batchStatus.status === 'failed'
                    ? 'bg-red-500'
                    : batchStatus.status === 'completed'
                    ? 'bg-green-500'
                    : 'bg-blue-500'
                }`}
                style={{ width: `${completedPct}%` }}
              />
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">{batchStatus.total_items}</p>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Total</p>
            </div>
            <div>
              <p className="font-semibold text-green-600 dark:text-green-400">{batchStatus.completed_items}</p>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Completed</p>
            </div>
            <div>
              <p className="font-semibold text-red-600 dark:text-red-400">{batchStatus.failed_items}</p>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Failed</p>
            </div>
          </div>

          {batchStatus.status === 'completed' && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={handleDownload}
                leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
                className="w-full"
              >
                Download Results
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function HistoryTab({
  page,
  onPageChange,
}: {
  page: number;
  onPageChange: (p: number) => void;
}) {
  const { data, isLoading } = useBatchHistory(page);
  const batches = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  async function handleDownload(batchId: string) {
    try {
      const blob = await integrationsService.downloadBatchResults(batchId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch-results-${batchId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download results');
    }
  }

  if (isLoading) {
    return (
      <Card padding="none">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100 dark:bg-gray-700" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-14 border-t border-gray-100 dark:border-gray-700 flex items-center px-6 gap-6">
              <div className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
              <div className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-12" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-12" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-12" />
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-20" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (batches.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No batch history</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Upload your first CSV file to get started.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="none">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Batch ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Completed
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Failed
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {batches.map((batch) => (
              <tr key={batch.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <td className="px-6 py-4 text-sm font-mono text-gray-900 dark:text-white">
                  {(batch.batch_id || batch.id).slice(0, 8)}...
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {formatRelativeTime(batch.created_at)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{batch.total_items}</td>
                <td className="px-6 py-4 text-sm text-green-600 dark:text-green-400">{batch.completed_items}</td>
                <td className="px-6 py-4 text-sm text-red-600 dark:text-red-400">{batch.failed_items}</td>
                <td className="px-6 py-4">
                  <Badge variant={getStatusVariant(batch.status)} size="sm">
                    {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  {batch.status === 'completed' && (
                    <button
                      onClick={() => handleDownload(batch.batch_id || batch.id)}
                      className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Download
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </Card>
  );
}
